import { defineStore } from "pinia";
import type { ErrorObject } from "@vuelidate/core";
import { TrainingsLmsApi, type TrainingLmsContentDetails, type TrainingPartnerListItem } from "@simbelapp/order-sdk";
import { useCategoriesStore } from "../categories/categories.store";
import { useSessionItemStore } from "../session-item/session-item-store";
import { useSessionsStore } from "../sessions/sessions-store";

import { useTrainingPartnersStore } from "../training-partners/training-partners.store";
import { useUsersStore } from "../users/users.store";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { usePagination } from "~/composables/paginations/usePagination";
import { useActivitiesStore } from "~/store/activities/activities.store";
import { useAuthStore } from "~/store/auth/auth.store";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import type { UserLanguageEnum } from "~/utils/enums/users.enums";
import { useTrainingBuilderValidation } from "~/composables/trainings/useTrainings";
import { SessionItemModalityEnum } from "~/utils/enums/session-items.enums";
import { BuilderTrainingNavEnum, TypeBuilderPanelEnum, VisibleStatus } from "~/utils/enums/training.enums";
import type { ICreateBuilderTraining, ITrainingSessionDraft } from "~/utils/interfaces/trainings-interface";
import type { ICompanyUsersSDK } from "~/utils/interfaces/users-interfaces";
import { useActivitiesValidation } from "~/composables/activities/useActivities";
import { useFilters } from "~/composables/filters/useFilters";

/** A blank draft for the « Ajouter une session » wizard: it picks the inscription type itself. */
function emptySessionDraft(): ITrainingSessionDraft {
  return {
    trainingAddress: null,
    sessionItemType: null,
    format: null,
    startDate: null,
    endDate: null,
    designation: null,
    overbookingNotAllowed: false,
    sessionTimeslot: [],
    currentSessionTimeslot: null,
  };
}

export const useCreateBuilderTrainingStore = defineStore("createBuilderTrainingStore", {
  state: (): ICreateBuilderTraining => {
    const authStore = useAuthStore();

    return {
      training: {
        content: {
          trainingId: null,
          title: null,
          trainingPartner: null,
          program: null,
          targetAudience: null,
          goals: [],
          language: (authStore.user?.language as UserLanguageEnum) ?? "fr",
          trainingAdmins: [],
        },
        sessionItems: {
          individual: {
            modality: null,
            priceExclTax: null,
            type: null,
            maxNbAttendees: null,
            minNbAttendees: null,
          },
        },
        modalities: {
          modality: null,
          durationDays: null,
          noRequest: null,
          comment: null,
        },
        advanced: {
          categories: null,
          skills: null,
          visibility: {
            visible: VisibleStatus.HIDDEN,
            groups: [],
          },
          trainingTags: [],
        },
        sessions: {
          sessionDraft: emptySessionDraft(),
          sessionList: [],
          catalogSessionsInPlan: [],
          catalogSessionsNotInPlan: [],
          showAddSession: false,
          showAddTimeslot: false,
          showEditTimeslotModalConfirm: null,
          showDeleteModal: false,
          showWarningDeleteModal: false,
          editTimeslot: null,
          currentModalConfirmIndex: null,
        },
        errors: {},
      },
      nav: BuilderTrainingNavEnum.CONTENT,
      showModal: false,
      showExit: false,
      showExitEdit: false,
      isEdit: false,
      readonly: false,
      currentTab: TypeBuilderPanelEnum.TRAINING,
      showPanel: false,
      isLoading: false,
    };
  },
  getters: {},
  actions: {
    async openTrainingModal(id?: string) {
      this.$reset();
      const router = useRouter();

      if (id) {
        const activitiesStore = useActivitiesStore();
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingsLmsApi);
        await apiInstance.validateTraining({ trainingId: id });
        await router.push({ query: { id } });
        await this.loadTraining(id);
        await activitiesStore.getActivitiesList(id);
        this.showPanel = true;
        this.isEdit = true;
      }

      this.showModal = true;
    },
    createTrainingAdmins(u: ICompanyUsersSDK) {
      const displayValue =
        u.firstname && u.lastname ? `${u.firstname} ${u.lastname}` : u.email ? `${u.email}` : (u.employeeNumber ?? "");
      const defaultUser: ICompanyUsersSDK = {
        userId: u.userId!,
        email: u.email ?? "",
        firstname: u.firstname ?? "",
        lastname: u.lastname ?? "",
        employeeNumber: u.employeeNumber ?? "",
        displayValue,
      };

      this.training.content.trainingAdmins.push(defaultUser);
    },
    async loadTraining(id: string) {
      this.training.sessions = {
        sessionDraft: null,
        sessionList: [],
        catalogSessionsInPlan: [],
        catalogSessionsNotInPlan: [],
        showAddSession: false,
        showAddTimeslot: false,
        showEditTimeslotModalConfirm: null,
        showDeleteModal: false,
        showWarningDeleteModal: false,
        editTimeslot: null,
        currentModalConfirmIndex: null,
      };

      await this.loadTrainingContent({ trainingId: id });
      await this.loadSessions(id);

      this.training.sessions.sessionDraft = emptySessionDraft();
    },

    /**
     * The Sessions tab is the same two catalogue tables as an external training, and `sessionList`
     * is deliberately NOT fetched here either: nothing renders it, and `addSession()` now tells a
     * creation succeeded from what `POST /sessions` answers rather than by re-reading the list.
     */
    async loadSessions(trainingId: string) {
      const sessionsStore = useSessionsStore();

      const catalogSessions = await sessionsStore.fetchCatalogSessions(trainingId);

      this.training.sessions.catalogSessionsInPlan = catalogSessions.inPlan;
      this.training.sessions.catalogSessionsNotInPlan = catalogSessions.notInPlan;
    },

    pushValidationErrors(errors: ErrorObject[]) {
      errors.forEach((err: ErrorObject) => {
        this.training.errors[err.$propertyPath] = err.$message as string;
      });
    },

    selectNewTrainingPartner(partnerName: string) {
      this.training.content.trainingPartner = {
        trainingPartnerId: null,
        name: partnerName,
      };
    },

    selectNewCategory(categoryName: string) {
      const categoryStore = useCategoriesStore();
      const alreadyExist =
        categoryStore.categories &&
        categoryStore.categories.find((cat) => {
          return cat.name === categoryName;
        });
      if (!alreadyExist) {
        const newCategory = {
          categoryId: "",
          name: categoryName,
          priority: 12,
          isDefault: false,
          isDeletable: true,
          isSimbelMaster: false,
        };

        this.training.advanced.categories = newCategory;
        categoryStore.categories && categoryStore.categories.push(newCategory);
      }
    },

    async addTraining() {
      const trainingValidation = useTrainingBuilderValidation();
      const feedback = useFeedback();
      const { content } = this.training;
      this.training.errors = {};
      await trainingValidation.validateStepAdvanced();

      if (!Object.keys(this.training.errors).length) {
        try {
          if (content.trainingId) {
            const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingsLmsApi);

            await apiInstance.validateTraining({ trainingId: content.trainingId });

            this.isEdit = true;
            this.nav = BuilderTrainingNavEnum.SESSIONS;

            feedback.success("La formation a bien été créée", "small");

            const router = useRouter();
            await router.push({
              query: { id: this.training.content.trainingId },
            });
            await this.loadTraining(content.trainingId);

            const paginate = usePagination();
            await paginate.fetchData();
          }
        } catch {
          feedback.error(`Une erreur est survenue`, "small");
        }
      }
    },
    async editTraining() {},

    async fetchSessionItems() {
      const { content, sessionItems } = this.training;
      if (content.trainingId) {
        const sessionItemStore = useSessionItemStore();
        const sessionItem = await sessionItemStore.findAllSessionItems(content.trainingId);
        if (!sessionItem) return;
        sessionItems.individual.minNbAttendees = sessionItem.minNbAttendees;
        sessionItems.individual.maxNbAttendees = sessionItem.maxNbAttendees;
        sessionItems.individual.priceExclTax = sessionItem.priceExclTax;
        sessionItems.individual.modality = sessionItem.modality;
        sessionItems.individual.type = sessionItem.type;
        sessionItems.individual.sessionItemId = sessionItem.sessionItemId;
      }
    },

    async addSession() {
      const feedback = useFeedback();

      if (this.nav === BuilderTrainingNavEnum.SESSIONS) {
        this.training.errors = {};
        await this.validation();
        if (
          !Object.keys(this.training.errors).length &&
          this.training.sessions.sessionDraft &&
          this.training.content.trainingId
        ) {
          this.training.sessions.sessionDraft.sessionItemId = this.training.sessionItems.individual
            .sessionItemId as string;

          if (!this.training.sessions.sessionDraft.trainingAddress) {
            this.training.sessions.sessionDraft.trainingAddress = this.training.sessionItems.individual
              .location as string;
          }

          const sessionStore = useSessionsStore();

          // A failed creation leaves the wizard open on its draft: closing it first would drop
          // everything the admin typed, with only a toast to say why.
          const created = await sessionStore.createSession(this.training.sessions.sessionDraft);
          if (!created) return;

          this.training.sessions.showAddSession = false;
          this.training.sessions.sessionDraft = emptySessionDraft();
          this.training.errors = {};

          // Through the store, so the two catalogue tables pick the new date up as well.
          await this.loadSessions(this.training.content.trainingId);

          feedback.success("La session a bien été ajoutée", "small");
        }
      }
    },
    async validation() {
      const trainingValidation = useTrainingBuilderValidation();
      const activitiesValidation = useActivitiesValidation();
      this.training.errors = {};
      if (this.nav === BuilderTrainingNavEnum.CONTENT) {
        await trainingValidation.validateStepContentFull();
        await activitiesValidation.validateActivity();
        await activitiesValidation.validateEmptyActivityQuiz();
      }
      if (this.nav === BuilderTrainingNavEnum.MODALITIES) await trainingValidation.validateStepModality();
      if (this.nav === BuilderTrainingNavEnum.ADVANCED) await trainingValidation.validateStepAdvanced();
      if (this.nav === BuilderTrainingNavEnum.SESSIONS) await trainingValidation.validateStepSession();
    },
    async assignTrainingAdmins(trainingAdminsIds: string[]) {
      const usersStore = useUsersStore();

      this.training.content.trainingAdmins = [];

      if (trainingAdminsIds?.length) {
        await usersStore.fetchCompanyUsersLazyWithStore(false, undefined, trainingAdminsIds);

        usersStore.companyUsers?.forEach((user) => {
          if (trainingAdminsIds?.includes(user.userId)) this.createTrainingAdmins(user);
        });
      } else {
        await usersStore.fetchCompanyUsersLazyWithStore(false);
      }
    },
    async assignPartner(trainingPartnerId: string | null) {
      const trainingPartnersStore = useTrainingPartnersStore();

      this.training.content.trainingPartner = null;

      if (trainingPartnerId) {
        await trainingPartnersStore.fetchTrainingPartnersLazyWithStore(trainingPartnerId);

        this.training.content.trainingPartner =
          (trainingPartnersStore.trainingPartners as TrainingPartnerListItem[])?.find(
            (partner) => partner.trainingPartnerId === trainingPartnerId,
          ) ?? null;
      } else {
        await trainingPartnersStore.fetchTrainingPartnersLazyWithStore();
      }
    },

    async assignCategory(categoryId: string | null) {
      const categoriesStore = useCategoriesStore();
      if (!categoriesStore?.categories?.length) {
        await categoriesStore.fetchCategories();
      }

      this.training.advanced.categories = null;

      if (categoryId) {
        this.training.advanced.categories =
          categoriesStore.categories?.find((category) => category.categoryId === categoryId) ?? null;
      }
    },

    async loadTrainingContent(parameters: { trainingId?: string; trainingContent?: TrainingLmsContentDetails }) {
      const { trainingId } = parameters;
      let { trainingContent } = parameters;

      if (!trainingContent && trainingId) {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingsLmsApi);
        trainingContent = await apiInstance.getLmsTrainingContent({ trainingId });
      }

      if (trainingContent) {
        const { content } = this.training;
        content.title = trainingContent.title || null;
        content.trainingId = trainingContent.trainingId;
        content.goals = trainingContent.goals || [];
        content.language = trainingContent.language as UserLanguageEnum;
        content.targetAudience = trainingContent.targetAudience || null;
        content.program = trainingContent.program || null;
        await this.assignTrainingAdmins(trainingContent.trainingAdminsIds || []);
        await this.assignPartner(trainingContent.trainingPartnerId || null);
      }
    },

    async loadTrainingModalities(trainingId: string) {
      if (trainingId) {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingsLmsApi);
        const trainingModalities = await apiInstance.getLmsTrainingModalities({ trainingId });

        if (trainingModalities) {
          const { modalities } = this.training;
          modalities.modality = (trainingModalities.modality as SessionItemModalityEnum) || null;
          modalities.durationDays = trainingModalities.durationDays || null;
          modalities.noRequest = trainingModalities.noRequest as boolean;
          modalities.comment = trainingModalities.comment || null;
        }
      }
    },

    async loadAdvancedInfos(trainingId: string) {
      if (trainingId) {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingsLmsApi);
        const trainingAdvancedInfos = await apiInstance.getTrainingLmsAdvancedInfos({ trainingId });

        if (trainingAdvancedInfos) {
          const { advanced } = this.training;

          await Promise.all([this.assignCategory(trainingAdvancedInfos.categoryId || null)]);
          advanced.trainingTags = trainingAdvancedInfos.trainingTags || [];

          if (trainingAdvancedInfos.visible) {
            if (trainingAdvancedInfos.groups?.length) {
              this.training.advanced.visibility = {
                visible: VisibleStatus.GROUPS,
                groups: trainingAdvancedInfos.groups?.map((g: any) => ({ groupId: g.groupId, name: g.name })),
              };
            } else {
              this.training.advanced.visibility = {
                visible: VisibleStatus.ALL,
                groups: [],
              };
            }
          } else {
            this.training.advanced.visibility = {
              visible: VisibleStatus.HIDDEN,
              groups: [],
            };
          }
        }
      }
    },

    async createTraining() {
      const trainingValidation = useTrainingBuilderValidation();
      this.training.errors = {};
      await trainingValidation.validateStepContent();
      const { content } = this.training;
      const feedback = useFeedback();

      if (!this.training.errors.length) {
        try {
          if (content.title) {
            const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingsLmsApi);
            const training = await apiInstance.createLmsTraining({
              createLMSTrainingDto: {
                title: content.title,
                trainingPartnerId: content.trainingPartner?.trainingPartnerId ?? null,
                trainingPartnerName:
                  content.trainingPartner?.name && !content.trainingPartner?.trainingPartnerId
                    ? content.trainingPartner?.name
                    : null,
              },
            });

            if (training) {
              await this.loadTrainingContent(training);
              feedback.success("Vos modifications ont bien été sauvegardées", "small");
            }
          }
        } catch {
          feedback.error(`Une erreur est survenue`, "small");
        }
      }
    },
    async updateTrainingStepContent(element: string) {
      const feedback = useFeedback();
      try {
        const { content } = this.training;

        let el = null;
        if (Object.prototype.hasOwnProperty.call(content, element)) {
          if (element === "trainingAdmins") {
            el = {
              trainingAdminsIds: content.trainingAdmins?.map((admin) => admin.userId) || [],
            };
          } else if (element === "trainingPartner") {
            el = {
              trainingPartnerId: content.trainingPartner?.trainingPartnerId ?? null,
              trainingPartnerName:
                content.trainingPartner?.name && !content.trainingPartner?.trainingPartnerId
                  ? content.trainingPartner?.name
                  : null,
            };
          } else {
            el = {
              [element]: content[element as keyof typeof content],
            };
          }
        }

        if (content.trainingId) {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingsLmsApi);
          await apiInstance.updateLmsTrainingContent({
            trainingId: content.trainingId,
            updateLMSTrainingContentDto: {
              ...el,
            },
          });
        }
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },
    async updateTrainingStepModality(element: string) {
      const feedback = useFeedback();
      try {
        const { modalities, content } = this.training;

        let el = null;
        if (Object.prototype.hasOwnProperty.call(modalities, element)) {
          el = {
            [element]: modalities[element as keyof typeof modalities],
          };
        }

        if (content.trainingId) {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingsLmsApi);
          await apiInstance.updateLmsTrainingModalities({
            trainingId: content.trainingId,
            updateLMSTrainingModalitiesDto: {
              ...el,
            },
          });
        }
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },

    async updateTrainingStepAdvanced(element: string) {
      const feedback = useFeedback();
      try {
        const { content, advanced } = this.training;

        let el = null;

        if (Object.prototype.hasOwnProperty.call(advanced, element)) {
          if (element === "categories") {
            el = {
              categoryId: advanced.categories?.categoryId || null,
            };
          } else if (element === "visibility") {
            // Extract groupIds from group objects (support both object format and legacy string format)
            const groupIds = advanced.visibility.groups?.map((g: any) => (typeof g === "string" ? g : g.groupId));
            el = {
              visible:
                advanced.visibility.visible === VisibleStatus.GROUPS ||
                advanced.visibility.visible === VisibleStatus.ALL,
              groupIds,
            };
          } else {
            el = {
              [element]: advanced[element as keyof typeof advanced],
            };
          }
        }

        if (content.trainingId) {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingsLmsApi);
          await apiInstance.updateLMSTrainingAdvancedInfosDto({
            trainingId: content.trainingId,
            updateLMSTrainingAdvancedInfosDto: {
              ...el,
            },
          });

          if (element === "visibility") {
            const filters = useFilters();
            await filters.fetchFilters("companyCatalog");
          }
        }
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },
    openAddSession() {
      this.training.sessions.showAddSession = !this.training.sessions.showAddSession;

      // The step dialogs are store-backed: a dropped draft must not leave one armed for the next
      // opening (Escape closes the wizard and its confirm dialog in one go).
      this.training.sessions.showAddTimeslot = false;
      this.training.sessions.editTimeslot = null;
      this.training.sessions.showEditTimeslotModalConfirm = null;
      this.training.sessions.currentModalConfirmIndex = null;
      this.training.sessions.showDeleteModal = false;
      this.training.sessions.showWarningDeleteModal = false;

      this.training.sessions.sessionDraft = emptySessionDraft();

      this.training.errors = {};
    },

    async removeTraining(trainingId: string) {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingsLmsApi);
        await apiInstance.removeTrainingLms({ trainingId });
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },
  },
});
